// fetch keepalive rejects bodies over 64KB; leave headroom
export const KEEPALIVE_LIMIT_BYTES = 60_000;

/** UTF-8 byte size of a string, which is how the server measures bodies. */
export function byteSize(text: string) {
  return new TextEncoder().encode(text).length;
}

/**
 * Gzips when the browser supports CompressionStream, buffered because
 * keepalive rejects streaming bodies. Undefined means "send it raw".
 */
async function gzipBody(text: string): Promise<Blob | undefined> {
  if (typeof CompressionStream === "undefined") return undefined;

  try {
    const stream = new Blob([text])
      .stream()
      .pipeThrough(new CompressionStream("gzip"));
    return await new Response(stream).blob();
  } catch {
    return undefined;
  }
}

export interface FetchKeepaliveOptions {
  method: "POST" | "PATCH";
  json: string;
  headers?: Record<string, string>;
  /** Ask for keepalive; only honoured when the wire size allows it. */
  keepalive?: boolean;
}

/**
 * Sends a JSON body gzipped (the server inflates it) and, when asked, with
 * keepalive so the request outlives the page.
 */
export async function fetchKeepalive(
  url: string,
  { method, json, headers, keepalive }: FetchKeepaliveOptions
): Promise<Response> {
  const gzipped = await gzipBody(json);
  const size = gzipped ? gzipped.size : byteSize(json);

  return fetch(url, {
    method,
    headers: {
      ...headers,
      "Content-Type": "application/json",
      ...(gzipped ? { "Content-Encoding": "gzip" } : {}),
    },
    body: gzipped ?? json,
    keepalive: !!keepalive && size < KEEPALIVE_LIMIT_BYTES,
  });
}
